// src/context/CartContext.js
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../utils/apiClient';
import { BASE_URL } from '../utils/config';
import { UserContext } from './UserContext';
import { NotificationContext } from './NotificationContext';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [itemCount, setItemCount] = useState(0);

  const { user } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);

  /**
   * A centralized function to update the cart state and ensure the item count is always in sync.
   * @param {object} newCart - The new cart object from the API.
   */
  const updateCartState = useCallback((newCart) => {
    setCart(newCart || { items: [] });
    setItemCount(newCart?.items?.length || 0);
  }, []);

  const fetchCart = useCallback(async () => {
    if (!user.isLoggedIn) {
      setCart({ items: [] });
      setItemCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await apiFetch(`${BASE_URL}/api/cart`, {}, user.token);
      if (response.ok) {
        const data = await response.json();
        updateCartState(data);
      } else {
        const data = await response.json();
        console.error('Failed to fetch cart:', data.message);
        updateCartState({ items: [] });
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
      addNotification('Could not load your shopping cart.', 'error');
      updateCartState({ items: [] });
    } finally {
      setLoading(false);
    }
  }, [user.isLoggedIn, user.token, addNotification, updateCartState]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(
    async (itemDetails, token) => {
      // itemDetails should be { productId, propertyId, inquiryId }
      // Log the token being used by the context right before the API call.
      console.log('[CartContext] addToCart called. Token passed in:', token);
      try {
        const url = `${BASE_URL}/api/cart/items`;
        // Sanitize the itemDetails to prevent circular JSON errors,
        // which can happen if an event object or other complex object is passed.
        const cleanItemDetails = {
          productId: itemDetails.productId,
          propertyId: itemDetails.propertyId,
          inquiryId: itemDetails.inquiryId,
          forceRepurchase: itemDetails.forceRepurchase,
        };
        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(cleanItemDetails),
        };

        const response = await apiFetch(url, options, token);

        const data = await response.json();
        if (response.ok) {
          updateCartState(data);
          // Use the name of the last item added for the notification
          addNotification(`'${data.items[data.items.length - 1].name}' added to cart.`, 'success');
          return { success: true, cart: data };
        } else {
          addNotification(data.message || 'Failed to add item to cart.', 'error');
          return { success: false, message: data.message };
        }
      } catch (error) {
        console.error('Error adding item to cart:', error);
        addNotification('A network error occurred while adding the item to your cart.', 'error');
        return { success: false, message: 'Network error.' };
      }
    },
    [addNotification, updateCartState]
  );

  const removeFromCart = useCallback(
    async (cartItemId) => {
      try {
        const response = await apiFetch(
          `${BASE_URL}/api/cart/items/${cartItemId}`,
          {
            method: 'DELETE',
          },
          user.token
        );
        const data = await response.json();
        if (response.ok) {
          updateCartState(data);
          return { success: true, cart: data };
        } else {
          addNotification(data.message || 'Failed to remove item from cart.', 'error');
          return { success: false, message: data.message };
        }
      } catch (error) {
        console.error('Error removing item from cart:', error);
        addNotification('A network error occurred while removing the item from your cart.', 'error');
        return { success: false, message: 'Network error.' };
      }
    },
    [user.token, addNotification, updateCartState]
  );

  const clearCart = useCallback(() => {
    updateCartState({ items: [] });
  }, [updateCartState]);

  const value = useMemo(
    () => ({
      cart,
      itemCount,
      loading,
      addToCart,
      removeFromCart,
      clearCart,
      updateCartState,
      fetchCart, // Expose fetchCart so other components can trigger a refresh
    }),
    // Add fetchCart to the dependency array of useMemo
    [cart, itemCount, loading, addToCart, removeFromCart, clearCart, updateCartState, fetchCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
